import React, { useState, useEffect, useMemo } from 'react';
import Icon from '../Icon';
import { C } from '../../utils/styles';
import KpiCard from '../common/KpiCard';
import StatusBadge from '../common/StatusBadge';
import api from '../../utils/api';
import { useConfig } from '../../context/ConfigContext';
import { optimizeEmployeeWithEligibility } from '../../utils/calculations';

export default function EnrollmentStep({ client, currentUser, onComplete, onRefresh }) {
  const [activeTab, setActiveTab] = useState('send');
  const [enrollmentStatus, setEnrollmentStatus] = useState(null);
  const [selectedEmployees, setSelectedEmployees] = useState([]);
  const [sending, setSending] = useState(false);
  const [credentials, setCredentials] = useState([]);
  const [showCredentials, setShowCredentials] = useState(false);

  useEffect(() => {
    loadEnrollmentStatus();
  }, [client]);

  const loadEnrollmentStatus = async () => {
    try {
      const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/clients/${client.id}/enrollment/status`, {
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('token')}`
        }
      });

      if (response.ok) {
        const data = await response.json();
        setEnrollmentStatus(data);
      }
    } catch (error) {
      console.error('Failed to load enrollment status:', error);
    }
  };

  const handleSendInvitations = async () => {
    if (selectedEmployees.length === 0) {
      alert('Please select at least one employee');
      return;
    }

    setSending(true);
    try {
      const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/clients/${client.id}/enrollment/invite`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${localStorage.getItem('token')}`
        },
        body: JSON.stringify({
          employeeIds: selectedEmployees,
          employerMessage: null,
          deadline: null
        })
      });

      if (response.ok) {
        const result = await response.json();
        setCredentials(result.credentials);
        setShowCredentials(true);
        setSelectedEmployees([]);
        await loadEnrollmentStatus();
        await onRefresh();
      } else {
        alert('Failed to send invitations');
      }
    } catch (error) {
      console.error('Error sending invitations:', error);
      alert('An error occurred');
    } finally {
      setSending(false);
    }
  };

  const employees = client.employees || [];
  const summary = enrollmentStatus?.summary || {};

  return (
    <div>
      <div
        style={{
          background: '#fff',
          border: `1px solid ${C.border}`,
          borderRadius: 12,
          padding: 32
        }}
      >
        <h2 style={{ fontSize: 20, fontWeight: 700, marginBottom: 8 }}>
          STEP 4: Employee Enrollment
        </h2>
        <p style={{ fontSize: 14, color: C.textMid, marginBottom: 24 }}>
          Send enrollment invitations to your employees and track their responses.
        </p>

        {/* KPI Summary */}
        {enrollmentStatus && (
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(150px, 1fr))', gap: 16, marginBottom: 32 }}>
            <KpiCard
              icon="people-outline"
              label="Total Employees"
              value={summary.total || 0}
              color="slate"
              dataTestId="tracking-kpi-total"
            />
            <KpiCard
              icon="send-outline"
              label="Invited"
              value={summary.invited || 0}
              color="blue"
              dataTestId="tracking-kpi-invited"
            />
            <KpiCard
              icon="checkmark-circle"
              label="Enrolled"
              value={summary.enrolled || 0}
              color="green"
              dataTestId="tracking-kpi-completed"
            />
            <KpiCard
              icon="close-circle-outline"
              label="Declined"
              value={summary.declined || 0}
              color="red"
            />
            <KpiCard
              icon="mail-outline"
              label="Pending"
              value={summary.not_invited || 0}
              color="orange"
            />
          </div>
        )}

        {/* Tabs */}
        <div style={{ display: 'flex', gap: 12, marginBottom: 24, borderBottom: `2px solid ${C.border}`, paddingBottom: 12 }}>
          <button
            onClick={() => setActiveTab('send')}
            style={{
              display: 'flex',
              alignItems: 'center',
              gap: 8,
              padding: '12px 20px',
              borderRadius: '8px 8px 0 0',
              border: 'none',
              background: activeTab === 'send' ? C.accent : 'transparent',
              color: activeTab === 'send' ? '#fff' : C.textMid,
              fontSize: 13,
              fontWeight: 700,
              cursor: 'pointer',
              position: 'relative',
              bottom: -2,
              borderBottom: activeTab === 'send' ? `3px solid ${C.accent}` : 'none',
              transition: 'background-color 0.15s, color 0.15s'
            }}
          >
            <Icon name="send-outline" style={{ fontSize: 16 }} />
            Send Invitations
          </button>

          <button
            onClick={() => setActiveTab('track')}
            style={{
              display: 'flex',
              alignItems: 'center',
              gap: 8,
              padding: '12px 20px',
              borderRadius: '8px 8px 0 0',
              border: 'none',
              background: activeTab === 'track' ? C.accent : 'transparent',
              color: activeTab === 'track' ? '#fff' : C.textMid,
              fontSize: 13,
              fontWeight: 700,
              cursor: 'pointer',
              position: 'relative',
              bottom: -2,
              borderBottom: activeTab === 'track' ? `3px solid ${C.accent}` : 'none',
              transition: 'background-color 0.15s, color 0.15s'
            }}
          >
            <Icon name="analytics-outline" style={{ fontSize: 16 }} />
            Track Enrollment
          </button>
        </div>

        {/* Tab Content */}
        {activeTab === 'send' && (
          <SendInvitationsTab
            client={client}
            employees={employees}
            selectedEmployees={selectedEmployees}
            setSelectedEmployees={setSelectedEmployees}
            onSend={handleSendInvitations}
            sending={sending}
            credentials={credentials}
            showCredentials={showCredentials}
            setShowCredentials={setShowCredentials}
            onRefresh={onRefresh}
          />
        )}

        {activeTab === 'track' && (
          <TrackEnrollmentTab
            employees={enrollmentStatus?.employees || []}
          />
        )}
      </div>
    </div>
  );
}

function SendInvitationsTab({ client, employees, selectedEmployees, setSelectedEmployees, onSend, sending, credentials, showCredentials, setShowCredentials, onRefresh }) {
  const { config } = useConfig();

  // Local state for inline email edits while typing (per-employee draft buffer).
  const [emailDrafts, setEmailDrafts] = useState({});
  const [savingEmail, setSavingEmail] = useState({});

  // ─── Eligibility filter ────────────────────────────────────────────────
  // Mirror Section 125 Summary's logic exactly: an employee is "eligible to
  // invite" only when running `optimizeEmployeeWithEligibility(emp, config)`
  // returns a viable optimal plan with `eligibility.eligible === true`.
  // Falls back to a stored `eligibility.eligible` flag when config is missing.
  const eligibleEmployees = useMemo(() => {
    if (!Array.isArray(employees) || employees.length === 0) return [];
    return employees.filter((e) => {
      // 1) Hard-no: explicit ineligibility flag wins immediately.
      if (e?.eligibility && e.eligibility.eligible === false) return false;

      // 2) If we have config, run the same live computation Section 125 uses.
      if (config) {
        const salaryNum = Number(e?.salary) || Number(e?.annualSalary) || 0;
        if (!salaryNum) return false;
        try {
          const a = optimizeEmployeeWithEligibility({ ...e, salary: salaryNum }, config);
          return !!(a?.eligibility?.eligible && a?.hasViable && a?.optimal);
        } catch (err) {
          // Fall through to stored flag below
        }
      }
      // 3) Last-resort: trust stored flag
      return e?.eligibility?.eligible === true;
    });
  }, [employees, config]);

  // ─── Identity & display helpers ────────────────────────────────────────
  // Census uploads use `name` (single field) + `employeeId`. Internal seed/test
  // data uses `firstName` + `lastName` + `id`. Support both transparently.
  const empKey = (e) => e?.id || e?.employeeId || e?.email || `row-${e?.row}`;
  const empDisplayName = (e) => {
    const fn = (e?.firstName || '').trim();
    const ln = (e?.lastName || '').trim();
    if (fn || ln) return `${fn} ${ln}`.trim();
    if (e?.name) return String(e.name).trim();
    if (e?.email) return e.email;
    if (e?.employeeId) return `Employee ${e.employeeId}`;
    return 'Employee';
  };

  // Selectable = eligible AND has a saved email
  const selectableIds = new Set(eligibleEmployees.filter((e) => !!e.email).map(empKey));
  const allSelected = selectableIds.size > 0 && selectedEmployees.length === selectableIds.size;
  const missingEmailCount = eligibleEmployees.filter((e) => !e.email).length;

  const toggleAll = () => {
    if (allSelected) {
      setSelectedEmployees([]);
    } else {
      setSelectedEmployees(Array.from(selectableIds));
    }
  };

  const toggleEmployee = (empId) => {
    if (!selectableIds.has(empId)) return;
    if (selectedEmployees.includes(empId)) {
      setSelectedEmployees(selectedEmployees.filter((id) => id !== empId));
    } else {
      setSelectedEmployees([...selectedEmployees, empId]);
    }
  };

  const saveEmail = async (emp) => {
    const key = empKey(emp);
    const draft = (emailDrafts[key] || '').trim();
    if (!draft || !draft.includes('@') || draft === emp.email) return;
    setSavingEmail((s) => ({ ...s, [key]: true }));
    try {
      // Patch using whichever identifier the backend will recognise. Backend
      // matches on either `id`, `employeeId`, or `email`.
      const lookup = emp.id || emp.employeeId || emp.email;
      await api.clients.updateEmployee(client.id, lookup, { email: draft });
      setEmailDrafts((d) => { const n = { ...d }; delete n[key]; return n; });
      if (onRefresh) await onRefresh();
    } catch (err) {
      console.error('Failed to save email:', err);
      alert('Could not save the email. Please try again.');
    } finally {
      setSavingEmail((s) => { const n = { ...s }; delete n[key]; return n; });
    }
  };

  if (eligibleEmployees.length === 0) {
    return (
      <div data-testid="enrollment-empty-state" style={{ textAlign: 'center', padding: 48 }}>
        <Icon name="cloud-upload-outline" style={{ fontSize: 48, color: 'rgb(203, 213, 225)', marginBottom: 16 }} />
        <div style={{ fontSize: 14, color: 'rgb(100, 116, 139)', marginBottom: 8, fontWeight: 700 }}>
          No eligible employees yet
        </div>
        <div style={{ fontSize: 13, color: 'rgb(148, 163, 184)' }}>
          Upload a census file in Step 3 to populate your eligible employee list here.
        </div>
      </div>
    );
  }

  return (
    <div>
      {/* Helper banner if some emails missing */}
      {missingEmailCount > 0 && (
        <div
          data-testid="missing-email-banner"
          style={{
            background: 'rgb(254, 243, 199)',
            border: '1px solid rgb(253, 230, 138)',
            borderRadius: 8,
            padding: '12px 16px',
            marginBottom: 16,
            display: 'flex', alignItems: 'center', gap: 10, fontSize: 13, color: 'rgb(120, 53, 15)',
          }}
        >
          <Icon name="information-circle-outline" style={{ fontSize: 18 }} />
          <span>
            <strong>{missingEmailCount}</strong> of <strong>{eligibleEmployees.length}</strong> eligible employee{eligibleEmployees.length !== 1 ? 's' : ''} {missingEmailCount === 1 ? "doesn't" : "don't"} have an email yet. Add one inline below to invite them.
          </span>
        </div>
      )}

      {/* Bulk Action Bar */}
      {selectedEmployees.length > 0 && (
        <div
          data-testid="bulk-selection-bar"
          style={{
            background: 'rgb(224, 242, 254)',
            border: '1px solid rgb(186, 230, 253)',
            borderRadius: 8,
            padding: 16,
            marginBottom: 16,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between'
          }}
        >
          <div style={{ fontSize: 14, fontWeight: 600, color: 'rgb(2, 132, 199)' }}>
            {selectedEmployees.length} employee{selectedEmployees.length !== 1 ? 's' : ''} selected
          </div>
          <div style={{ display: 'flex', gap: 8 }}>
            <button
              onClick={() => setSelectedEmployees([])}
              style={{
                padding: '8px 16px',
                fontSize: 12,
                fontWeight: 600,
                color: 'rgb(71, 85, 105)',
                background: 'transparent',
                border: '1px solid rgb(226, 232, 240)',
                borderRadius: 6,
                cursor: 'pointer',
                transition: 'background-color 0.15s'
              }}
            >
              Clear
            </button>
            <button
              data-testid="send-invitations-button"
              onClick={onSend}
              disabled={sending}
              style={{
                padding: '8px 16px',
                fontSize: 12,
                fontWeight: 600,
                color: '#fff',
                background: sending ? 'rgb(203, 213, 225)' : 'rgb(2, 132, 199)',
                border: 'none',
                borderRadius: 6,
                cursor: sending ? 'not-allowed' : 'pointer',
                display: 'flex',
                alignItems: 'center',
                gap: 6,
                transition: 'background-color 0.15s'
              }}
            >
              <Icon name="send-outline" style={{ fontSize: 14 }} />
              {sending ? 'Sending...' : 'Send Invitations'}
            </button>
          </div>
        </div>
      )}

      {/* Employee Table */}
      <div style={{ border: '1px solid rgb(226, 232, 240)', borderRadius: 8, overflow: 'hidden' }}>
        <table style={{ width: '100%', borderCollapse: 'collapse' }}>
          <thead>
            <tr style={{ background: 'rgb(248, 250, 252)', borderBottom: '1px solid rgb(226, 232, 240)' }}>
              <th style={{ padding: 12, textAlign: 'left', width: 40 }}>
                <input
                  type="checkbox"
                  data-testid="employee-select-all-checkbox"
                  checked={allSelected}
                  onChange={toggleAll}
                  disabled={selectableIds.size === 0}
                  style={{ accentColor: 'rgb(2, 132, 199)', width: 16, height: 16 }}
                />
              </th>
              <th style={{ padding: 12, textAlign: 'left', fontSize: 12, fontWeight: 600, color: 'rgb(100, 116, 139)', textTransform: 'uppercase', letterSpacing: '0.05em' }}>Name</th>
              <th style={{ padding: 12, textAlign: 'left', fontSize: 12, fontWeight: 600, color: 'rgb(100, 116, 139)', textTransform: 'uppercase', letterSpacing: '0.05em' }}>Email</th>
              <th style={{ padding: 12, textAlign: 'left', fontSize: 12, fontWeight: 600, color: 'rgb(100, 116, 139)', textTransform: 'uppercase', letterSpacing: '0.05em' }}>Status</th>
            </tr>
          </thead>
          <tbody>
            {eligibleEmployees.map((emp, idx) => {
              const key = empKey(emp);
              const hasEmail = !!emp.email;
              const isSelected = selectedEmployees.includes(key);
              const draft = emailDrafts[key] ?? '';
              const isSaving = !!savingEmail[key];
              return (
                <tr
                  key={key}
                  data-testid={`enrollment-employee-row-${key}`}
                  style={{
                    borderBottom: idx < eligibleEmployees.length - 1 ? '1px solid rgb(226, 232, 240)' : 'none',
                    background: isSelected ? 'rgb(240, 253, 250)' : (hasEmail ? '#fff' : 'rgb(255, 251, 235)')
                  }}
                >
                  <td style={{ padding: 12 }}>
                    <input
                      type="checkbox"
                      data-testid="employee-row-checkbox"
                      checked={isSelected}
                      disabled={!hasEmail}
                      onChange={() => toggleEmployee(key)}
                      style={{ accentColor: 'rgb(14, 116, 144)', width: 16, height: 16, cursor: hasEmail ? 'pointer' : 'not-allowed', opacity: hasEmail ? 1 : 0.5 }}
                      title={hasEmail ? '' : 'Add an email before selecting'}
                    />
                  </td>
                  <td style={{ padding: 12, fontSize: 14, color: 'rgb(15, 23, 42)' }}>
                    {empDisplayName(emp)}
                  </td>
                  <td style={{ padding: 12, fontSize: 14, color: 'rgb(71, 85, 105)' }}>
                    {hasEmail ? (
                      emp.email
                    ) : (
                      <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                        <input
                          type="email"
                          data-testid={`enrollment-email-input-${key}`}
                          value={draft}
                          placeholder="Add email…"
                          onChange={(e) => setEmailDrafts((d) => ({ ...d, [key]: e.target.value }))}
                          onKeyDown={(e) => { if (e.key === 'Enter') saveEmail(emp); }}
                          disabled={isSaving}
                          style={{
                            flex: 1,
                            padding: '6px 10px',
                            border: '1px solid rgb(252, 211, 77)',
                            borderRadius: 6,
                            fontSize: 13,
                            background: '#fff',
                            outline: 'none',
                          }}
                        />
                        <button
                          type="button"
                          data-testid={`enrollment-email-save-${key}`}
                          onClick={() => saveEmail(emp)}
                          disabled={isSaving || !draft || !draft.includes('@')}
                          style={{
                            padding: '6px 12px',
                            fontSize: 12,
                            fontWeight: 700,
                            color: '#fff',
                            background: (isSaving || !draft || !draft.includes('@')) ? 'rgb(203, 213, 225)' : 'rgb(2, 132, 199)',
                            border: 'none',
                            borderRadius: 6,
                            cursor: (isSaving || !draft || !draft.includes('@')) ? 'not-allowed' : 'pointer',
                          }}
                        >
                          {isSaving ? 'Saving…' : 'Save'}
                        </button>
                      </div>
                    )}
                  </td>
                  <td style={{ padding: 12 }}>
                    {hasEmail ? (
                      <StatusBadge status={emp.enrollmentStatus || 'not_invited'} size="small" />
                    ) : (
                      <span style={{ fontSize: 11, fontWeight: 700, color: 'rgb(180, 83, 9)', textTransform: 'uppercase', letterSpacing: '0.05em' }}>Email needed</span>
                    )}
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>

      {/* Credentials Modal */}
      {showCredentials && credentials.length > 0 && (
        <div
          style={{
            position: 'fixed',
            inset: 0,
            background: 'rgba(0, 0, 0, 0.5)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            zIndex: 1000,
            padding: 20
          }}
          onClick={() => setShowCredentials(false)}
        >
          <div
            style={{
              background: '#fff',
              borderRadius: 12,
              maxWidth: 700,
              width: '100%',
              maxHeight: '80vh',
              overflow: 'auto',
              padding: 32
            }}
            onClick={(e) => e.stopPropagation()}
          >
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 24 }}>
              <h3 style={{ fontSize: 18, fontWeight: 700, margin: 0 }}>
                Invitations Sent Successfully
              </h3>
              <button
                onClick={() => setShowCredentials(false)}
                style={{
                  background: 'transparent',
                  border: 'none',
                  cursor: 'pointer',
                  padding: 8
                }}
              >
                <Icon name="close-outline" style={{ fontSize: 24, color: 'rgb(100, 116, 139)' }} />
              </button>
            </div>

            <div style={{ background: 'rgb(254, 243, 199)', border: '1px solid rgb(253, 230, 138)', borderRadius: 8, padding: 16, marginBottom: 24 }}>
              <div style={{ display: 'flex', gap: 12 }}>
                <Icon name="information-circle-outline" style={{ fontSize: 20, color: 'rgb(217, 119, 6)' }} />
                <div style={{ fontSize: 13, color: 'rgb(120, 53, 15)', lineHeight: 1.5 }}>
                  <strong>Demo Mode:</strong> Below are the temporary login credentials for each employee. In production, these would be sent via email.
                </div>
              </div>
            </div>

            <div style={{ fontSize: 12, fontWeight: 600, color: 'rgb(100, 116, 139)', marginBottom: 12, textTransform: 'uppercase', letterSpacing: '0.05em' }}>
              Employee Login Credentials
            </div>

            <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
              {credentials.map((cred, idx) => (
                <div
                  key={idx}
                  style={{
                    border: '1px solid rgb(226, 232, 240)',
                    borderRadius: 8,
                    padding: 16,
                    background: 'rgb(248, 250, 252)'
                  }}
                >
                  <div style={{ fontSize: 14, fontWeight: 600, marginBottom: 8, color: 'rgb(15, 23, 42)' }}>
                    {cred.firstName} {cred.lastName}
                  </div>
                  <div style={{ fontSize: 13, color: 'rgb(71, 85, 105)', marginBottom: 4 }}>
                    Email: <strong>{cred.email}</strong>
                  </div>
                  <div style={{ fontSize: 13, color: 'rgb(71, 85, 105)', marginBottom: 4 }}>
                    Temporary Password: <code style={{ background: '#fff', padding: '2px 6px', borderRadius: 4, fontFamily: 'JetBrains Mono, monospace' }}>{cred.tempPassword}</code>
                  </div>
                  <div style={{ fontSize: 12, color: 'rgb(100, 116, 139)' }}>
                    Login at: {cred.loginLink}
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

function TrackEnrollmentTab({ employees }) {
  if (employees.length === 0) {
    return (
      <div style={{ textAlign: 'center', padding: 48 }}>
        <Icon name="analytics-outline" style={{ fontSize: 48, color: 'rgb(203, 213, 225)', marginBottom: 16 }} />
        <div style={{ fontSize: 14, color: 'rgb(100, 116, 139)' }}>
          No enrollment data yet
        </div>
      </div>
    );
  }

  return (
    <div>
      <div style={{ border: '1px solid rgb(226, 232, 240)', borderRadius: 8, overflow: 'hidden' }}>
        <table data-testid="tracking-status-table" style={{ width: '100%', borderCollapse: 'collapse' }}>
          <thead>
            <tr style={{ background: 'rgb(248, 250, 252)', borderBottom: '1px solid rgb(226, 232, 240)' }}>
              <th style={{ padding: 12, textAlign: 'left', fontSize: 12, fontWeight: 600, color: 'rgb(100, 116, 139)', textTransform: 'uppercase', letterSpacing: '0.05em' }}>
                Name
              </th>
              <th style={{ padding: 12, textAlign: 'left', fontSize: 12, fontWeight: 600, color: 'rgb(100, 116, 139)', textTransform: 'uppercase', letterSpacing: '0.05em' }}>
                Email
              </th>
              <th style={{ padding: 12, textAlign: 'left', fontSize: 12, fontWeight: 600, color: 'rgb(100, 116, 139)', textTransform: 'uppercase', letterSpacing: '0.05em' }}>
                Status
              </th>
              <th style={{ padding: 12, textAlign: 'left', fontSize: 12, fontWeight: 600, color: 'rgb(100, 116, 139)', textTransform: 'uppercase', letterSpacing: '0.05em' }}>
                Invited
              </th>
              <th style={{ padding: 12, textAlign: 'left', fontSize: 12, fontWeight: 600, color: 'rgb(100, 116, 139)', textTransform: 'uppercase', letterSpacing: '0.05em' }}>
                Submitted
              </th>
            </tr>
          </thead>
          <tbody>
            {employees.map((emp, idx) => (
              <tr
                key={emp.id}
                style={{
                  borderBottom: idx < employees.length - 1 ? '1px solid rgb(226, 232, 240)' : 'none'
                }}
              >
                <td style={{ padding: 12, fontSize: 14, color: 'rgb(15, 23, 42)' }}>
                  {emp.firstName} {emp.lastName}
                </td>
                <td style={{ padding: 12, fontSize: 14, color: 'rgb(71, 85, 105)' }}>
                  {emp.email}
                </td>
                <td style={{ padding: 12 }}>
                  <StatusBadge status={emp.status} size="small" />
                </td>
                <td style={{ padding: 12, fontSize: 13, color: 'rgb(100, 116, 139)' }}>
                  {emp.invitedAt ? new Date(emp.invitedAt).toLocaleDateString() : '—'}
                </td>
                <td style={{ padding: 12, fontSize: 13, color: 'rgb(100, 116, 139)' }}>
                  {emp.enrollmentSubmittedAt ? new Date(emp.enrollmentSubmittedAt).toLocaleDateString() : '—'}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}
